import { toolbarExpandedPref } from "../preferences";

/** Minimum descriptor Toolbar needs per top-level button (issue
 *  #150). The Toolbar only cares about id (for matching the
 *  pre-rendered `data-tool-id`), label (for tooltips), and the
 *  optional shortcut hint. Concrete Tool instances are handled
 *  by `src/app.ts`. */
export interface ToolbarEntry {
  id: string;
  label: string;
  shortcut?: string;
}

export interface ToolbarOptions {
  tools: ToolbarEntry[];
  onSelect: (toolId: string) => void;
  initial: string;
}

export class Toolbar {
  private root: HTMLElement;
  private buttons = new Map<string, HTMLButtonElement>();
  private toolsById = new Map<string, ToolbarEntry>();
  private currentId: string;
  private collapseToggle: HTMLButtonElement;
  private expanded: boolean;
  /** Hover-capable, fine-pointer devices (mouse / trackpad) typically have a
   *  keyboard too — we use this to decide whether to surface the keyboard
   *  shortcut hint in the tooltip. Touch-only devices get the bare label so
   *  "(R)" doesn't read as a misleading instruction. */
  private keyboardLikely = window.matchMedia(
    "(hover: hover) and (pointer: fine)",
  );

  constructor(root: HTMLElement, options: ToolbarOptions) {
    this.root = root;
    this.currentId = options.initial;
    // Restore the persisted expanded/collapsed state — default to collapsed
    // (icon-only) so first-time users see the original compact toolbar.
    this.expanded = toolbarExpandedPref.get();
    // Toolbar markup lives in index.html (issue #181) — every button
    // is pre-rendered with its icon + label + data-tool-id so the
    // toolbar paints before JS runs. Here we just attach handlers
    // and track state. A registered tool with no corresponding
    // markup is a wiring bug; we warn and skip it rather than
    // create the button on the fly so the static HTML stays the
    // single source of truth.
    for (const tool of options.tools) {
      const btn = root.querySelector<HTMLButtonElement>(
        `.tool-btn[data-tool-id="${tool.id}"]`,
      );
      if (!btn) {
        console.warn(`Toolbar: no static button for tool "${tool.id}"`);
        continue;
      }
      btn.addEventListener("click", () => {
        this.setActive(tool.id);
        options.onSelect(tool.id);
      });
      this.buttons.set(tool.id, btn);
      this.toolsById.set(tool.id, tool);
    }

    // Collapse / expand toggle is also pre-rendered in index.html —
    // we just find it, attach the click handler, and lift the static
    // `disabled` attribute now that the toggle is interactive (issue
    // #181).
    this.collapseToggle = root.querySelector<HTMLButtonElement>(
      ".tool-collapse-toggle",
    )!;
    this.collapseToggle.addEventListener("click", () => {
      this.setExpanded(!this.expanded);
    });
    this.collapseToggle.disabled = false;

    this.applyExpandedState();
    this.refreshTooltips();
    // Re-run the tooltip text when a Magic Keyboard is attached/detached
    // mid-session so the shortcut hint appears or disappears on next hover.
    this.keyboardLikely.addEventListener("change", () =>
      this.refreshTooltips(),
    );
    this.setActive(options.initial);
  }

  private setExpanded(next: boolean) {
    if (this.expanded === next) return;
    this.expanded = next;
    toolbarExpandedPref.set(next);
    this.applyExpandedState();
    this.refreshTooltips();
  }

  private applyExpandedState() {
    // The `#app.toolbar-expanded` class is the single switch — CSS
    // (issue #181) handles which collapse-toggle icon and label
    // text are visible at any time so JS never edits the toggle's
    // contents.
    document
      .getElementById("app")
      ?.classList.toggle("toolbar-expanded", this.expanded);
  }

  private refreshTooltips() {
    const showShortcut = this.keyboardLikely.matches;
    for (const [id, btn] of this.buttons) {
      const tool = this.toolsById.get(id);
      if (!tool) continue;
      btn.title =
        showShortcut && tool.shortcut
          ? `${tool.label} (${tool.shortcut.toUpperCase()})`
          : tool.label;
    }
    this.collapseToggle.title = this.expanded
      ? "Collapse sidebar"
      : "Expand sidebar";
  }

  setActive(id: string) {
    this.currentId = id;
    for (const [tid, btn] of this.buttons) {
      btn.classList.toggle("active", tid === id);
    }
  }

  get active(): string {
    return this.currentId;
  }

  /** Underlying button element for a tool id — used by callers that want to
   *  anchor a popup or fly-out next to a specific tool. Returns null when the
   *  id isn't registered. */
  getButton(id: string): HTMLButtonElement | null {
    return this.buttons.get(id) ?? null;
  }

  /** Toggle the disabled state on a tool button. Disabled buttons swallow
   *  clicks (so the tool can't be activated) and pick up the `:disabled`
   *  styling for visual feedback. No-op when the id isn't registered. */
  setDisabled(id: string, disabled: boolean) {
    const btn = this.buttons.get(id);
    if (btn) btn.disabled = disabled;
  }
}
